/**
 * Device error type hierarchy.
 *
 * Provides a consistent set of error classes for all device managers
 * (WebSerial, WebBLE, WebUSB-FTDI). Each error carries the originating
 * device type and optionally wraps an underlying cause.
 */

/**
 * Identifier for the device transport that produced the error.
 */
export type DeviceType = 'webserial' | 'webble' | 'webusb-ftdi';

/**
 * Base class for all device-related errors.
 *
 * The original error (if any) is preserved on `cause` so callers can
 * inspect platform-specific details without losing the high-level context.
 */
export class DeviceError extends Error {
    public readonly deviceType: DeviceType;
    public override readonly cause?: Error;

    constructor(message: string, deviceType: DeviceType, cause?: unknown) {
        super(message);
        this.name = 'DeviceError';
        this.deviceType = deviceType;
        if (cause instanceof Error) {
            this.cause = cause;
        } else if (cause !== undefined) {
            this.cause = new Error(stringifyCause(cause));
        }
    }
}

/**
 * Safely convert an arbitrary value to a string without relying on Object's
 * default `[object Object]` representation.
 */
function stringifyCause(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    if (value === null) {
        return 'null';
    }
    try {
        return JSON.stringify(value) ?? 'unknown cause';
    } catch {
        return 'unknown cause';
    }
}

/**
 * Thrown when the required browser API is not available.
 */
export class DeviceNotSupportedError extends DeviceError {
    constructor(deviceType: DeviceType, message: string) {
        super(message, deviceType);
        this.name = 'DeviceNotSupportedError';
    }
}

/**
 * Thrown when establishing a connection to a device fails.
 */
export class DeviceConnectionError extends DeviceError {
    constructor(deviceType: DeviceType, message: string, cause?: unknown) {
        super(message, deviceType, cause);
        this.name = 'DeviceConnectionError';
    }
}

/**
 * Thrown when an operation is attempted while no device is connected,
 * or when the device unexpectedly disconnects.
 */
export class DeviceDisconnectedError extends DeviceError {
    constructor(deviceType: DeviceType, message = 'Not connected to a device', cause?: unknown) {
        super(message, deviceType, cause);
        this.name = 'DeviceDisconnectedError';
    }
}

/**
 * Thrown when sending or receiving data with a connected device fails.
 */
export class DeviceCommunicationError extends DeviceError {
    constructor(deviceType: DeviceType, message: string, cause?: unknown) {
        super(message, deviceType, cause);
        this.name = 'DeviceCommunicationError';
    }
}

/**
 * Thrown when `connect()` is invoked while a connection attempt is
 * already in progress.
 */
export class DeviceBusyError extends DeviceError {
    constructor(deviceType: DeviceType, message = 'Connection already in progress') {
        super(message, deviceType);
        this.name = 'DeviceBusyError';
    }
}

/**
 * Extract a human-readable message from an arbitrary thrown value.
 */
export function toErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}
